import MultiSelectList from 'component/atom/forms/select/multi-select-list/MultiSelectList';
import React, { useRef, useEffect, useState, useMemo } from 'react';
import styles from '../CaptureForm.module.scss';
import Toggle from 'component/atom/forms/toggle/Toggle';
import Tooltip from 'component/Custom/tooltip/Tooltip';
import SvgWithBranding from 'component/hoc/branding/withBrandingColor.hoc';
import Select from 'component/atom/forms/select/select/Select';
import Input from 'component/atom/forms/input/input/Input';
import EntityItem from './entity-item/EntityItem';

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const Entities = ({ formik, data }) => {
  const [openIndex, setOpenIndex] = useState(null);
  const containerRef = useRef(null);

  const entities = useMemo(
    () => formik.values?.suggestedEntityQuestions || [],
    [formik.values?.suggestedEntityQuestions],
  );

  const typeOptions =
    data?.data?.items?.map((item) => ({
      value: toNum(item.variableId),
      label: item.variableName,
    })) || [];

  const selectedIds = entities
    .map((q) => toNum(q.variableId))
    .filter(Number.isInteger);

  const sortedEntities = useMemo(() => {
    return [...entities].sort((a, b) => (a.orderNo || 0) - (b.orderNo || 0));
  }, [entities]);

  const handleVariablesChange = (e) => {
    const rawValues = Array.isArray(e)
      ? e
      : (e?.target?.value ?? e?.value ?? []);

    const values = (rawValues || [])
      .map((v) => toNum(v))
      .filter(Number.isInteger);

    const current = formik.values?.suggestedEntityQuestions || [];

    let next = current.filter((q) => values.includes(toNum(q.variableId)));

    const added = values.filter(
      (id) => !current.some((q) => toNum(q.variableId) === id),
    );

    if (added.length > 0) {
      const maxOrder =
        next.length > 0 ? Math.max(...next.map((q) => q.orderNo || 0)) : 0;

      const newItems = added.map((id, idx) => ({
        variableId: id,
        question: '',
        validation_ErrorMessage: '',
        orderNo: maxOrder + idx + 1,
        isLookupEnabled: false,
        validationRule: '',
        lookups: [],
      }));

      next = [...next, ...newItems];
    }

    formik.setFieldValue('suggestedEntityQuestions', next);
    if (next.length === 0) setOpenIndex(null);
  };

  useEffect(() => {
    const handleOutside = (e) => {
      if (
        openIndex !== null &&
        containerRef.current &&
        !containerRef.current.contains(e.target)
      ) {
        setOpenIndex(null);
      }
    };

    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [openIndex]);

  return (
    <div className={styles['entire-reply']}>
      <MultiSelectList
        label='Variable'
        placeholder='Select Variables'
        name='entityVariables'
        options={typeOptions}
        value={selectedIds}
        onChange={handleVariablesChange}
        showTags={false}
      />

      <div ref={containerRef} className={styles['entity-list']}>
        {sortedEntities.map((item) => {
          const actualIndex = entities.findIndex(
            (q) => toNum(q.variableId) === toNum(item.variableId),
          );

          const label =
            typeOptions.find((o) => toNum(o.value) === toNum(item.variableId))
              ?.label || item.variableId;

          return (
            <EntityItem
              key={item.id ?? item.variableId ?? item.orderNo}
              index={actualIndex}
              item={item}
              label={label}
              formik={formik}
              typeOptions={typeOptions}
              openIndex={openIndex}
              setOpenIndex={setOpenIndex}
            />
          );
        })}
      </div>

      <div className={styles.suggest}>
        <div className={styles['tooltip-container']}>
          <p>Automatically Re-prompt</p>
          <Tooltip content='Automatic reprompts use AI to guide users in completing all required entities.'>
            <SvgWithBranding type='info' color='#0744E1' width={14} />
          </Tooltip>
        </div>
        <Toggle
          name='entities_Reprompt'
          value={formik.values.entities_Reprompt || false}
          onToggleChange={() =>
            formik.setFieldValue(
              'entities_Reprompt',
              !formik.values.entities_Reprompt,
            )
          }
        />
      </div>

      <Select
        label='Max Retries'
        placeholder='Select Max Retries'
        options={[
          { label: '1', value: 1 },
          { label: '2', value: 2 },
          { label: '3', value: 3 },
        ]}
        name='entities_MaxRetries'
        value={formik.values.entities_MaxRetries}
        onChange={formik.handleChange}
        error={formik.errors?.entities_MaxRetries}
        touched={formik.touched?.entities_MaxRetries}
      />

      <div className={styles.suggest}>
        <div className={styles['tooltip-container']}>
          <p>Save as Form Response</p>
          <Tooltip content='Toggling this on will save the collected entities in a smart form'>
            <SvgWithBranding type='info' color='#0744E1' width={14} />
          </Tooltip>
        </div>
        <Toggle />
      </div>

      <Input
        label='Form Name'
        name='entities_FormName'
        value={formik.values.entities_FormName}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.errors?.entities_FormName}
        touched={formik.touched?.entities_FormName}
      />
    </div>
  );
};

export default Entities;
